import { useState, useRef, useEffect, useMemo } from 'react';
import { Minimize2, Maximize2 } from 'lucide-react';

export const Minimap = ({
  objects,
  pan,
  setPan,
  zoom,
  canvasWidth = 1920,
  canvasHeight = 800,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const minimapRef = useRef(null);
  
  // Minimap dimensions
  const MINIMAP_WIDTH = 200;
  const MINIMAP_HEIGHT = 140;
  const PADDING = 50;

  // Calculate bounds of all objects
  const bounds = useMemo(() => {
    const nonConnectorObjects = objects.filter(o => o.type !== 'connector');
    
    if (nonConnectorObjects.length === 0) {
      return { minX: -500, minY: -300, maxX: 1500, maxY: 900 };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    nonConnectorObjects.forEach(obj => {
      minX = Math.min(minX, obj.x);
      minY = Math.min(minY, obj.y);
      maxX = Math.max(maxX, obj.x + (obj.width || 100));
      maxY = Math.max(maxY, obj.y + (obj.height || 100));
    });

    // Handle connectors
    objects.filter(o => o.type === 'connector').forEach(obj => {
      if (obj.startX !== undefined) {
        minX = Math.min(minX, obj.startX, obj.endX || obj.startX);
        minY = Math.min(minY, obj.startY, obj.endY || obj.startY);
        maxX = Math.max(maxX, obj.startX, obj.endX || obj.startX);
        maxY = Math.max(maxY, obj.startY, obj.endY || obj.startY);
      }
    });

    // Add padding and ensure minimum size
    minX = Math.min(minX - PADDING, -200);
    minY = Math.min(minY - PADDING, -200);
    maxX = Math.max(maxX + PADDING, 1200);
    maxY = Math.max(maxY + PADDING, 800);

    return { minX, minY, maxX, maxY };
  }, [objects]);

  // Calculate scale to fit content in minimap
  const contentWidth = bounds.maxX - bounds.minX;
  const contentHeight = bounds.maxY - bounds.minY;
  const scale = Math.min(MINIMAP_WIDTH / contentWidth, MINIMAP_HEIGHT / contentHeight);

  // Calculate viewport rectangle in minimap coordinates
  const viewportInMinimap = useMemo(() => {
    const viewportWidth = canvasWidth / zoom;
    const viewportHeight = canvasHeight / zoom;
    const viewportX = -pan.x / zoom;
    const viewportY = -pan.y / zoom;

    return {
      x: (viewportX - bounds.minX) * scale,
      y: (viewportY - bounds.minY) * scale,
      width: viewportWidth * scale,
      height: viewportHeight * scale,
    };
  }, [pan, zoom, bounds, scale, canvasWidth, canvasHeight]);

  // Handle minimap click to navigate
  const handleMinimapClick = (e) => {
    if (!minimapRef.current || isDragging) return;
    
    const rect = minimapRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert click position to canvas coordinates
    const canvasX = clickX / scale + bounds.minX;
    const canvasY = clickY / scale + bounds.minY;

    // Center viewport on clicked position
    const viewportWidth = canvasWidth / zoom;
    const viewportHeight = canvasHeight / zoom;
    
    setPan({
      x: -(canvasX - viewportWidth / 2) * zoom,
      y: -(canvasY - viewportHeight / 2) * zoom,
    });
  };

  // Handle viewport drag
  const handleViewportMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      if (!minimapRef.current) return;
      
      const rect = minimapRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Convert to canvas coordinates
      const canvasX = clickX / scale + bounds.minX;
      const canvasY = clickY / scale + bounds.minY;

      // Center viewport on drag position
      const viewportWidth = canvasWidth / zoom;
      const viewportHeight = canvasHeight / zoom;
      
      setPan({
        x: -(canvasX - viewportWidth / 2) * zoom,
        y: -(canvasY - viewportHeight / 2) * zoom,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, scale, bounds, zoom, canvasWidth, canvasHeight, setPan]);

  // Get color for object type
  const getObjectColor = (obj) => {
    if (obj.type === 'sticky') return obj.color || '#FEF3C7';
    if (obj.type === 'shape') return obj.fill || '#FFFFFF';
    if (obj.type === 'text') return '#1E293B';
    if (obj.type === 'drawing') return obj.stroke || '#1E293B';
    if (obj.type === 'frame') return 'transparent';
    if (obj.type === 'image') return '#CBD5E1';
    return '#E2E8F0';
  };

  const getStrokeColor = (obj) => {
    if (obj.type === 'sticky') return 'transparent';
    return obj.stroke || '#94A3B8';
  };

  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="fixed bottom-4 right-4 z-40 bg-white rounded-lg shadow-lg border border-gray-200 p-2.5 hover:bg-gray-50 transition-colors"
        title="Show Minimap"
        data-testid="minimap-expand"
      >
        <Maximize2 size={18} className="text-gray-600" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-40 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{ width: MINIMAP_WIDTH + 2 }}
      data-testid="minimap"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 border-b border-gray-100">
        <span className="text-xs font-medium text-gray-500">Overview</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setIsCollapsed(true)}
            className="p-0.5 hover:bg-gray-200 rounded transition-colors"
            title="Collapse"
            data-testid="minimap-collapse"
          >
            <Minimize2 size={14} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* Minimap Canvas */}
      <div
        ref={minimapRef}
        className="relative cursor-pointer"
        style={{ 
          width: MINIMAP_WIDTH, 
          height: MINIMAP_HEIGHT,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }}
        onClick={handleMinimapClick}
      >
        {/* Grid pattern */}
        <svg
          width={MINIMAP_WIDTH}
          height={MINIMAP_HEIGHT}
          className="absolute inset-0 opacity-30"
        >
          <defs>
            <pattern
              id="minimap-grid"
              width={20 * scale}
              height={20 * scale}
              patternUnits="userSpaceOnUse"
            >
              <circle cx={0.5} cy={0.5} r={0.5} fill="#94A3B8" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#minimap-grid)" />
        </svg>

        {/* Objects */}
        <svg
          width={MINIMAP_WIDTH}
          height={MINIMAP_HEIGHT}
          className="absolute inset-0"
        >
          {/* Render connectors first (below objects) */}
          {objects.filter(obj => obj.type === 'connector').map(conn => {
            const startX = ((conn.startX || 0) - bounds.minX) * scale;
            const startY = ((conn.startY || 0) - bounds.minY) * scale;
            const endX = ((conn.endX || 0) - bounds.minX) * scale;
            const endY = ((conn.endY || 0) - bounds.minY) * scale;
            
            return (
              <line
                key={conn.id}
                x1={startX}
                y1={startY}
                x2={endX}
                y2={endY}
                stroke="#94A3B8"
                strokeWidth={1}
              />
            );
          })}

          {/* Render objects */}
          {objects.filter(obj => obj.type !== 'connector').map(obj => {
            const x = (obj.x - bounds.minX) * scale;
            const y = (obj.y - bounds.minY) * scale;
            const width = Math.max((obj.width || 50) * scale, 2);
            const height = Math.max((obj.height || 50) * scale, 2);
            const color = getObjectColor(obj);
            const strokeColor = getStrokeColor(obj);

            if (obj.type === 'drawing' && obj.points?.length > 0) {
              // Render drawing as simplified path
              const points = obj.points.map(p => ({
                x: ((obj.x + (p.x || 0)) - bounds.minX) * scale,
                y: ((obj.y + (p.y || 0)) - bounds.minY) * scale,
              }));
              
              if (points.length < 2) return null;
              
              const pathD = points.reduce((d, p, i) => 
                d + (i === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), 
              '');
              
              return (
                <path
                  key={obj.id}
                  d={pathD}
                  stroke={color}
                  strokeWidth={1}
                  fill="none"
                />
              );
            }

            // Get border radius based on shape
            let rx = 0;
            if (obj.type === 'sticky') rx = 2;
            else if (obj.shape === 'circle') rx = Math.min(width, height) / 2;
            else if (obj.shape === 'diamond') {
              // Render diamond as polygon
              const cx = x + width / 2;
              const cy = y + height / 2;
              return (
                <polygon
                  key={obj.id}
                  points={`${cx},${y} ${x + width},${cy} ${cx},${y + height} ${x},${cy}`}
                  fill={color}
                  stroke={strokeColor}
                  strokeWidth={0.5}
                />
              );
            }

            return (
              <rect
                key={obj.id}
                x={x}
                y={y}
                width={width}
                height={height}
                fill={color}
                stroke={strokeColor}
                strokeWidth={0.5}
                rx={rx}
              />
            );
          })}
        </svg>

        {/* Viewport rectangle with outside dimming */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: 0,
            top: 0,
            width: MINIMAP_WIDTH,
            height: MINIMAP_HEIGHT,
          }}
        >
          {/* Dimmed areas outside viewport */}
          <div 
            className="absolute bg-black/20"
            style={{
              left: 0,
              top: 0,
              width: Math.max(0, viewportInMinimap.x),
              height: MINIMAP_HEIGHT,
            }}
          />
          <div 
            className="absolute bg-black/20"
            style={{
              left: Math.max(0, viewportInMinimap.x + viewportInMinimap.width),
              top: 0,
              right: 0,
              height: MINIMAP_HEIGHT,
            }}
          />
          <div 
            className="absolute bg-black/20"
            style={{
              left: Math.max(0, viewportInMinimap.x),
              top: 0,
              width: Math.min(viewportInMinimap.width, MINIMAP_WIDTH),
              height: Math.max(0, viewportInMinimap.y),
            }}
          />
          <div 
            className="absolute bg-black/20"
            style={{
              left: Math.max(0, viewportInMinimap.x),
              top: Math.min(MINIMAP_HEIGHT, viewportInMinimap.y + viewportInMinimap.height),
              width: Math.min(viewportInMinimap.width, MINIMAP_WIDTH),
              bottom: 0,
            }}
          />
        </div>

        {/* Draggable viewport rectangle */}
        <div
          className={`absolute border-2 border-blue-500 rounded-sm cursor-move transition-shadow ${
            isDragging ? 'shadow-lg' : ''
          }`}
          style={{
            left: Math.max(0, Math.min(viewportInMinimap.x, MINIMAP_WIDTH - 10)),
            top: Math.max(0, Math.min(viewportInMinimap.y, MINIMAP_HEIGHT - 10)),
            width: Math.max(10, Math.min(viewportInMinimap.width, MINIMAP_WIDTH - viewportInMinimap.x)),
            height: Math.max(10, Math.min(viewportInMinimap.height, MINIMAP_HEIGHT - viewportInMinimap.y)),
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
          }}
          onMouseDown={handleViewportMouseDown}
          data-testid="minimap-viewport"
        />
      </div>
    </div>
  );
};
